import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import './DocsPage.css';
import useSpeech from './hooks/useSpeech';

// Task ID to section mapping (based on the markdown structure)
const taskSections = {
    '1-2.1': { start: '## 1-2.1 - Panel Admin Web estable', end: '---' },
    '1-2.2': { start: '## 1-2.2 - Diseño responsive', end: '---' },
    '1-2.3': { start: '## 1-2.3 - Branding finalizado', end: '═' },
    '3.0': { start: '## 3.0 - Rutas:', end: '---' },
    '3.1': { start: '## 3.1 - Motor de Viabilidad', end: '---' },
    '3.2': { start: '## 3.2 - Conexión WhatsApp META API', end: '---' },
    '3.3.1': { start: '## 3.3.1 - Base de datos multi-dirección', end: '---' },
    '3.3.2': { start: '## 3.3.2 - Reconocimiento', end: '---' },
    '3.3.3': { start: '## 3.3.3 - Bot conversacional', end: '---' },
    '3.3.4': { start: '## 3.3.4 - Validación teléfono único', end: '---' },
    '3.3.5': { start: '## 3.3.5 - Notificación ON_ASSIGN', end: '---' },
    '3.3.6': { start: '## 3.3.6 - Recordatorio 24h', end: '---' },
    '3.3.7': { start: '## 3.3.7 - Notificación', end: '---' },
    '3.3.8': { start: '## 3.3.8 - Agenda Global', end: '---' },
    '3.3.9': { start: '## 3.3.9 - Bot PRO', end: '---' },
    '3.3.10': { start: '## 3.3.10 - Consulta agenda', end: '---' },
    '3.3.11': { start: '## 3.3.11 - Reserva automática', end: '---' },
    '3.3.12': { start: '## 3.3.12 - Autopilot Total', end: '═' },
};

const DocsPage = () => {
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [taskTitle, setTaskTitle] = useState('');
    const { taskId } = useParams();
    const navigate = useNavigate();
    const contentRef = useRef(null);
    const { toggle: toggleSpeech, isSpeaking, stop: stopSpeech } = useSpeech();

    useEffect(() => {
        fetch('/docs.md')
            .then(res => res.text())
            .then(text => {
                if (taskId) {
                    // Extract specific task section
                    const section = extractTaskSection(text, taskId);
                    setContent(section.content);
                    setTaskTitle(section.title);
                } else {
                    setContent(text);
                    setTaskTitle('Guía Completa');
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Error loading docs:', err);
                setContent('Error al cargar la documentación.');
                setLoading(false);
            });
    }, [taskId]);

    // Extract a specific task section from the full markdown
    const extractTaskSection = (fullText, id) => {
        const lines = fullText.split('\n');
        let capturing = false;
        let sectionLines = [];
        let title = `Tarea ${id}`;

        // Find the section that starts with ## {id}
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Check if this line starts the section we want
            if (line.startsWith(`## ${id}`) || line.startsWith(`## ${id} `)) {
                capturing = true;
                // Extract title from the heading
                title = line.replace(/^##\s*/, '').trim();
            }

            // If we're capturing, add the line
            if (capturing) {
                // Stop at the next major section delimiter
                if (sectionLines.length > 0 && (
                    line.startsWith('## ') ||
                    line.startsWith('═══') ||
                    line.startsWith('# FASE')
                )) {
                    break;
                }
                sectionLines.push(line);
            }
        }

        // If we didn't find a specific section, try broader search
        if (sectionLines.length === 0) {
            for (let i = 0; i < lines.length; i++) {
                if (lines[i].includes(id)) {
                    capturing = true;
                    // Go back a few lines to get the heading
                    const startIdx = Math.max(0, i - 2);
                    for (let j = startIdx; j < Math.min(lines.length, i + 100); j++) {
                        if (lines[j].startsWith('## ') && j > i) break;
                        if (lines[j].startsWith('---') && j > i + 10) break;
                        sectionLines.push(lines[j]);
                    }
                    break;
                }
            }
        }

        return {
            content: sectionLines.length > 0
                ? sectionLines.join('\n')
                : `No se encontró documentación para la tarea ${id}.\n\n[Ver documentación completa](/docs)`,
            title
        };
    };

    // Handle PDF download
    const handleDownloadPDF = () => {
        // Use browser print to PDF
        const printContent = contentRef.current;
        if (!printContent) return;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>FIXARR - ${taskId ? taskTitle : 'Documentación Completa'}</title>
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                        line-height: 1.6;
                        padding: 40px;
                        max-width: 800px;
                        margin: 0 auto;
                        color: #1a1a1a;
                    }
                    h1 { color: #3b82f6; font-size: 24px; margin-top: 32px; }
                    h2 { color: #6366f1; font-size: 20px; margin-top: 24px; }
                    h3 { color: #8b5cf6; font-size: 16px; margin-top: 16px; }
                    code { 
                        background: #f1f5f9; 
                        padding: 2px 6px; 
                        border-radius: 4px;
                        font-family: 'Courier New', monospace;
                    }
                    pre { 
                        background: #f1f5f9; 
                        padding: 16px; 
                        border-radius: 8px;
                        overflow-x: auto;
                    }
                    ul, ol { padding-left: 24px; }
                    hr { border: none; border-top: 1px solid #e2e8f0; margin: 24px 0; }
                    .header { 
                        border-bottom: 2px solid #3b82f6; 
                        padding-bottom: 16px; 
                        margin-bottom: 32px;
                    }
                    .header h1 { margin: 0; color: #1a1a1a; }
                    .header p { color: #64748b; margin: 8px 0 0; }
                    @media print {
                        body { padding: 20px; }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>📖 FIXARR - ${taskId ? taskTitle : 'Documentación'}</h1>
                    <p>Generado el ${new Date().toLocaleDateString('es-ES')}</p>
                </div>
                ${printContent.innerHTML}
            </body>
            </html>
        `);
        printWindow.document.close();

        // Wait for content to load then trigger print
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    if (loading) {
        return (
            <div className="docs-container">
                <div className="docs-bg">
                    <div className="docs-grid-overlay"></div>
                    <div className="docs-orb orb-1"></div>
                    <div className="docs-orb orb-2"></div>
                </div>
                <div className="loading">⏳ Cargando documentación...</div>
            </div>
        );
    }

    return (
        <div className="docs-container">
            {/* Background Effects */}
            <div className="docs-bg">
                <div className="docs-grid-overlay"></div>
                <div className="docs-orb orb-1"></div>
                <div className="docs-orb orb-2"></div>
            </div>

            {/* Compact Header */}
            <header className="docs-header">
                <div className="docs-header-left">
                    <button className="docs-back-btn" onClick={() => navigate('/')}>
                        ← Roadmap
                    </button>
                    {taskId && (
                        <button className="docs-back-btn" onClick={() => navigate('/docs')}>
                            📚 Ver todo
                        </button>
                    )}
                </div>
                <div className="docs-header-center">
                    <h1>{taskId ? `📋 ${taskTitle}` : '📖 Documentación'}</h1>
                </div>
                <div className="docs-header-right">
                    <button
                        className={`docs-speak-btn ${isSpeaking ? 'speaking' : ''}`}
                        onClick={() => {
                            if (isSpeaking) {
                                stopSpeech();
                            } else {
                                toggleSpeech(content, 'docs-content');
                            }
                        }}
                        title={isSpeaking ? 'Detener lectura' : 'Leer en voz alta'}
                    >
                        {isSpeaking ? '🔊' : '🔈'}
                    </button>
                    <button className="docs-pdf-btn" onClick={handleDownloadPDF}>
                        📄 Descargar PDF
                    </button>
                </div>
            </header>

            {/* Content */}
            <main className="docs-content" ref={contentRef}>
                <ReactMarkdown
                    components={{
                        h1: ({ children }) => <h1 className="doc-h1">{children}</h1>,
                        h2: ({ children }) => <h2 className="doc-h2">{children}</h2>,
                        h3: ({ children }) => <h3 className="doc-h3">{children}</h3>,
                        code: ({ inline, className, children, ...props }) => {
                            if (inline) {
                                return <code className="inline-code" {...props}>{children}</code>;
                            }
                            return (
                                <pre className="code-block">
                                    <code {...props}>{children}</code>
                                </pre>
                            );
                        },
                        ul: ({ children }) => <ul className="doc-list">{children}</ul>,
                        ol: ({ children }) => <ol className="doc-list">{children}</ol>,
                        strong: ({ children }) => <strong className="doc-strong">{children}</strong>,
                    }}
                >
                    {content}
                </ReactMarkdown>
            </main>

            {/* Footer */}
            <footer className="docs-footer">
                <span>📖 FIXARR Docs</span>
                <span>•</span>
                <span>Sistema de Gestión de Servicios Técnicos</span>
            </footer>
        </div>
    );
};

export default DocsPage;
